import ConvertHtmlToPlainText from './ConvertHtmlToPlainText';

const QUOTE_REGEX = /["“”]/;

export const hasQuoteCharacter = (html) =>
  QUOTE_REGEX.test(ConvertHtmlToPlainText(html || ''));

export const endsWithQuestionMark = (html) => {
  const plainText = ConvertHtmlToPlainText(html || '').trim();
  return plainText.length > 0 && plainText.endsWith('?');
};

const QUOTE_MESSAGE = 'Contains a quote character (" or smart quotes).';
const QUESTION_MARK_MESSAGE = 'Should end with a question mark (?).';
const DUPLICATE_MESSAGE = 'Duplicate answer text.';

export const getTextContentErrorMessages = (html) => {
  const messages = [];
  if (hasQuoteCharacter(html)) messages.push(QUOTE_MESSAGE);
  return messages;
};

export const getQuestionContentErrorMessages = (html) => {
  const messages = getTextContentErrorMessages(html);
  if (!endsWithQuestionMark(html)) messages.push(QUESTION_MARK_MESSAGE);
  return messages;
};

export const getAnswerContentErrorMessages = (answer, duplicateAnswers) => {
  const messages = getTextContentErrorMessages(answer?.text);
  if (duplicateAnswers?.has(answer)) messages.push(DUPLICATE_MESSAGE);
  return messages;
};

export const getDuplicateAnswers = (answers) => {
  const duplicates = new Set();
  if (!answers || answers.length < 2) {
    return duplicates;
  }

  const plainTexts = answers.map((answer) =>
    ConvertHtmlToPlainText(answer?.text || '').trim(),
  );

  for (let i = 0; i < plainTexts.length; i++) {
    if (!plainTexts[i]) continue;
    for (let j = i + 1; j < plainTexts.length; j++) {
      if (plainTexts[i] === plainTexts[j]) {
        duplicates.add(answers[i]);
        duplicates.add(answers[j]);
      }
    }
  }

  return duplicates;
};

const isEmptyText = (html) => ConvertHtmlToPlainText(html || '').trim() === '';

const getNonEmptyValue = (text, altText) => text || altText;

/**
 * Rules that prevent an assignment from being submitted.
 *
 * Submitting is accepted or rejected by the API, which enforces the same
 * rules (see `api/utils/questionValidation.js` in the API repository) so that
 * a client cannot patch the status directly and skip them. They are kept here
 * as well because Run validation reports them alongside the content issues,
 * which never leave the admin. Changing a rule means changing both.
 */
export const getQuestionBlockingErrors = (question) => {
  const errors = [];

  if (!question.softwarePage || !question.documentPage) {
    errors.push('missing Software Page or Document Page');
  }

  if (isEmptyText(question.notes)) {
    errors.push('missing Further Reading');
  }

  const hasSubstantialChanges =
    question.substantialChanges ||
    (question.answers || []).some((answer) => answer.substantialChanges);

  if (hasSubstantialChanges && isEmptyText(question.aiAdminFeedback)) {
    errors.push('marked as Significant Changes but missing AI Feedback');
  }

  return errors;
};

/**
 * Content rules (quotes, duplicate answers, missing question mark). These
 * never block saving or submitting; the writer decides whether to fix them.
 */
export const getQuestionContentIssues = (question) => {
  const errors = [];

  const questionDisplayText = question.multipleOptionMode
    ? getNonEmptyValue(question.text, question.flashQuestionText)
    : getNonEmptyValue(question.flashQuestionText, question.text);
  getQuestionContentErrorMessages(questionDisplayText).forEach((message) =>
    errors.push(`question text - ${message}`),
  );

  const duplicateAnswers = getDuplicateAnswers(question.answers);
  (question.answers || []).forEach((answer, index) => {
    getAnswerContentErrorMessages(answer, duplicateAnswers).forEach((message) =>
      errors.push(`answer ${index + 1} - ${message}`),
    );
  });

  getTextContentErrorMessages(question.notes).forEach((message) =>
    errors.push(`Further Reading - ${message}`),
  );

  getTextContentErrorMessages(question.aiAdminFeedback).forEach((message) =>
    errors.push(`AI Feedback - ${message}`),
  );

  return errors;
};

/** Every issue on a question: blocking errors first, then content issues. */
export const getQuestionValidationIssues = (question) => [
  ...getQuestionBlockingErrors(question),
  ...getQuestionContentIssues(question),
];

const getChapterEntries = (questions, getReasons) =>
  (questions || [])
    .filter((question) => !question.deletedAt)
    .map((question) => ({
      questionId: question.id,
      presentationIndex: question.presentationIndex,
      reasons: getReasons(question),
    }))
    .filter((entry) => entry.reasons.length > 0);

/** Full report (blocking + content) — used by Run Validation. */
export const getChapterValidationReport = (questions) =>
  getChapterEntries(questions, getQuestionValidationIssues);

/**
 * Short wording for a set of issues, e.g. "14 issues across 6 questions".
 * Shared by Run validation and by the rejected submission message, so both
 * read the same way; the detail lives in the grid tooltips and in the
 * assignment comments respectively.
 */
export const getValidationSummary = (issueCount, questionCount) =>
  `${issueCount} ${issueCount === 1 ? 'issue' : 'issues'} across ` +
  `${questionCount} ${questionCount === 1 ? 'question' : 'questions'}`;
